import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { MultiPreviewEntry } from "../types/api";
import FilePicker from "./FilePicker";

interface Props {
  title: string;
  hint: string;
  insurerName: string;
  files: File[];
  onChange: (files: File[]) => void;
}

const ACCEPT = ".xlsx,.xls,.xlsb,.zip";

function size(n: number): string {
  return n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
}

// One statement's files (the workbook plus any partner SOAs): add more, remove one, and
// see which sheets the insurer's layout will read before running.
export default function StatementFiles({ title, hint, insurerName, files, onChange }: Props) {
  const [preview, setPreview] = useState<MultiPreviewEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!files.length || !insurerName) { setPreview([]); setErr(""); return; }
    let live = true;
    setBusy(true);
    setErr("");
    api.previewMulti(insurerName, files)
      .then((r) => { if (live) setPreview(r.preview); })
      .catch((e) => { if (live) { setPreview([]); setErr(e instanceof Error ? e.message : String(e)); } })
      .finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [files, insurerName]);

  function add(more: File[]) {
    const have = new Set(files.map((f) => `${f.name}|${f.size}`));
    onChange([...files, ...more.filter((f) => !have.has(`${f.name}|${f.size}`))]);
  }

  const read = preview.filter((p) => p.matched);
  const ignored = preview.filter((p) => !p.matched && !p.unreadable);
  const perFile = (name: string) => read.filter((p) => p.fname === name).length;
  const unreadable = new Set(preview.filter((p) => p.unreadable).map((p) => p.fname));

  return (
    <div className="sf">
      <div className="sf-head">
        <span className="sf-title">{title}</span>
        {files.length > 0 && (
          <button type="button" className="sf-clear" onClick={() => onChange([])}>Remove all</button>
        )}
      </div>
      <FilePicker accept={ACCEPT} multiple required={!files.length}
        status={err ? "error" : files.length ? "done" : undefined}
        label={files.length ? "Add more files" : "Drop the statement here, or browse"}
        onFiles={add} />
      <p className="sf-hint">{hint}</p>
      {files.length > 0 && (
        <ul className="sf-list">
          {files.map((f, i) => {
            const n = perFile(f.name);
            const bad = unreadable.has(f.name);
            return (
              <li key={`${f.name}|${f.size}`} className={`sf-file${bad ? " sf-file--bad" : ""}`}>
                <span className="sf-file-icon" aria-hidden="true">{bad ? "!" : "▤"}</span>
                <span className="sf-file-name" title={f.name}>{f.name}</span>
                <span className="sf-file-meta">
                  {bad ? "cannot be read" : busy ? "scanning…" : `${n} ${n === 1 ? "sheet" : "sheets"} · ${size(f.size)}`}
                </span>
                <button type="button" className="sf-remove" aria-label={`Remove ${f.name}`}
                  onClick={() => onChange(files.filter((_, j) => j !== i))}>×</button>
              </li>
            );
          })}
        </ul>
      )}
      {err && <p className="sf-err">{err}</p>}
      {!busy && read.length > 0 && (
        <details className="sf-sheets">
          <summary>
            {read.length} {read.length === 1 ? "sheet" : "sheets"} will be read
            {ignored.length ? ` · ${ignored.length} ignored` : ""}
          </summary>
          <ul>
            {read.map((p) => (
              <li key={`${p.fname}|${p.sheet}`}>
                {p.sheet}{p.account ? <span className="sf-acc"> {p.account.replace(/_/g, " ")}</span> : null}
                {files.length > 1 && <span className="sf-of"> · {p.fname}</span>}
              </li>
            ))}
            {ignored.map((p) => (
              <li key={`${p.fname}|${p.sheet}`} className="sf-ignored">{p.sheet} · not part of the layout</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
